import { useCallback, useEffect, useMemo, useRef, useState } from "react";

interface DraggableGearProps {
  /** Diameter of the main (red, draggable) gear, in px. */
  size?: number;
}

type GearVariant = "red" | "black" | "ink" | "accent" | "secondary";

interface SatelliteSpec {
  id: string;
  size: number;
  teeth: number;
  /** CSS angle around the main gear center (0 = right, 90 = down). */
  angle: number;
  variant: GearVariant;
  /** Initial rotation so teeth visually mesh with main at rest. */
  phase: number;
}

interface ChainedSpec {
  id: string;
  parentId: string;
  size: number;
  teeth: number;
  /** CSS angle around the parent satellite's center. */
  angleFromParent: number;
  variant: GearVariant;
  /** Initial rotation so teeth visually mesh with parent at rest. */
  phase: number;
}

const VARIANT_CLASSES: Record<GearVariant, { body: string; border: string; marker: string }> = {
  red: {
    body: "bg-primary-500",
    border: "border-primary-500",
    marker: "bg-foreground-950",
  },
  black: {
    body: "bg-foreground-950",
    border: "border-foreground-950",
    marker: "bg-background-50",
  },
  ink: {
    body: "bg-foreground-800",
    border: "border-foreground-800",
    marker: "bg-background-50",
  },
  accent: {
    body: "bg-accent-500",
    border: "border-accent-500",
    marker: "bg-foreground-950",
  },
  secondary: {
    body: "bg-secondary-500",
    border: "border-secondary-500",
    marker: "bg-foreground-950",
  },
};

interface GearVisualProps {
  size: number;
  teeth: number;
  rotation: number;
  variant: GearVariant;
  showBolt?: boolean;
  showMarker?: boolean;
}

function GearVisual({
  size,
  teeth,
  rotation,
  variant,
  showBolt = false,
  showMarker = true,
}: GearVisualProps) {
  const cls = VARIANT_CLASSES[variant];
  // Tooth size scales with module (size/teeth) so meshing looks consistent.
  const toothSize = (size / teeth) * 1.55;

  const teethElements = Array.from({ length: teeth }, (_, i) => {
    const angle = (360 / teeth) * i;
    return (
      <div
        key={i}
        className="absolute top-1/2 left-1/2 origin-center"
        style={{
          transform: `translate(-50%, -50%) rotate(${angle}deg) translateY(-${size / 2}px)`,
        }}
      >
        <div
          className={`${cls.body} rounded-md`}
          style={{ width: toothSize, height: toothSize }}
        />
      </div>
    );
  });

  return (
    <div
      className="relative w-full h-full"
      style={{
        transform: `rotate(${rotation}deg)`,
        willChange: "transform",
      }}
    >
      {teethElements}

      {/* Body disk */}
      <div className={`absolute inset-0 rounded-full ${cls.body}`} />

      {/* Inner cutout */}
      <div
        className="absolute rounded-full bg-background-50"
        style={{ top: "22%", left: "22%", right: "22%", bottom: "22%" }}
      />

      {/* Inner ring stroke */}
      <div
        className={`absolute rounded-full border-[5px] ${cls.border}`}
        style={{ top: "30%", left: "30%", right: "30%", bottom: "30%" }}
      />

      {/* Center bolt (main gear only) */}
      {showBolt && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div
            className={`relative rounded-full ${cls.marker} flex items-center justify-center`}
            style={{ width: "14%", height: "14%" }}
          >
            <div className="absolute top-1 left-1/2 -translate-x-1/2 w-[3px] h-[20%] bg-background-50 rounded-full" />
            <div className="w-[28%] h-[28%] rounded-full bg-background-50" />
          </div>
        </div>
      )}

      {/* Orientation marker dot at top of gear (rotates with it) */}
      {showMarker && (
        <div
          className={`absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1 rounded-full ${cls.marker} z-20`}
          style={{ width: Math.max(6, size * 0.045), height: Math.max(6, size * 0.045) }}
        />
      )}
    </div>
  );
}

export default function DraggableGear({ size = 460 }: DraggableGearProps) {
  const MAIN_TEETH = 12;

  /**
   * Direct satellites of the main red gear — three of them.
   * All sized at the same module (size / teeth) as the main so teeth mesh
   * cleanly at the pitch distance (mainR + satR). They counter-rotate at
   * the ratio MAIN_TEETH / satTeeth.
   */
  const satellites: SatelliteSpec[] = useMemo(() => {
    const mod = size / MAIN_TEETH;

    // Phase so a satellite tooth lands in the gap between main teeth at rest.
    const computePhase = (cssAngle: number, satTeeth: number) => {
      const raw = cssAngle - 90 + 180 / satTeeth;
      const pitch = 360 / satTeeth;
      let p = raw % pitch;
      if (p < 0) p += pitch;
      return p;
    };

    const specs: Array<Omit<SatelliteSpec, "size" | "phase">> = [
      // medium gear, upper-right of main — warm accent
      { id: "s1", teeth: 9, angle: -60, variant: "accent" },
      // small gear, lower-left of main — supporting secondary tone
      { id: "s2", teeth: 6, angle: 150, variant: "secondary" },
      // LARGE gear, lower-right of main — deep ink for weight
      { id: "s3", teeth: 10, angle: 60, variant: "ink" },
    ];

    return specs.map((spec) => ({
      ...spec,
      size: Math.round(spec.teeth * mod),
      phase: computePhase(spec.angle, spec.teeth),
    }));
  }, [size]);

  /**
   * Chained gears — mesh with a satellite (not with the main). They get
   * driven THROUGH their parent satellite. Direction reverses on each hop,
   * so a chained gear rotates the same way as the main gear, just faster.
   */
  const chained: ChainedSpec[] = useMemo(() => {
    const mod = size / MAIN_TEETH;

    // Phase so the chained gear's tooth sits in the parent gear's gap,
    // accounting for the parent's own resting phase.
    const computeChainedPhase = (
      cssAngleFromParent: number,
      parentTeeth: number,
      parentPhase: number,
      childTeeth: number
    ) => {
      const raw =
        cssAngleFromParent -
        90 +
        180 / childTeeth +
        parentPhase * (parentTeeth / childTeeth);
      const pitch = 360 / childTeeth;
      let p = raw % pitch;
      if (p < 0) p += pitch;
      return p;
    };

    // c1 is a tiny gear meshing with s2 (the existing small black gear),
    // tucked further out in the lower-left so it doesn't crash into anything.
    const s2 = satellites.find((s) => s.id === "s2");
    if (!s2) return [];

    const c1Teeth = 4;
    const c1AngleFromParent = 240; // down-left from s2

    return [
      {
        id: "c1",
        parentId: "s2",
        teeth: c1Teeth,
        size: Math.round(c1Teeth * mod),
        angleFromParent: c1AngleFromParent,
        // tiny chained gear — stays black so the chain reaction reads clearly
        variant: "black" as GearVariant,
        phase: computeChainedPhase(
          c1AngleFromParent,
          s2.teeth,
          s2.phase,
          c1Teeth
        ),
      },
    ];
  }, [satellites, size]);

  // Container must fit the main gear plus the farthest gear (satellite or chained).
  const containerSize = useMemo(() => {
    const halfSize = size / 2;

    const satReach = satellites.map(
      (s) => (size + s.size) / 2 + s.size / 2
    );

    const chainedReach = chained.map((c) => {
      const parent = satellites.find((s) => s.id === c.parentId);
      if (!parent) return 0;
      // Parent center distance from main center
      const pDist = (size + parent.size) / 2;
      const pRad = (parent.angle * Math.PI) / 180;
      const px = Math.cos(pRad) * pDist;
      const py = Math.sin(pRad) * pDist;
      // Child position relative to parent
      const cDist = (parent.size + c.size) / 2;
      const cRad = (c.angleFromParent * Math.PI) / 180;
      const cx = px + Math.cos(cRad) * cDist;
      const cy = py + Math.sin(cRad) * cDist;
      return Math.sqrt(cx * cx + cy * cy) + c.size / 2;
    });

    const maxReach = Math.max(halfSize, ...satReach, ...chainedReach);
    return Math.ceil(maxReach + 24) * 2;
  }, [satellites, chained, size]);

  const [rotation, setRotation] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);

  const mainGearRef = useRef<HTMLDivElement>(null);
  const centerRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const lastAngleRef = useRef<number>(0);
  const accumulatedRef = useRef<number>(0);
  const velocityRef = useRef<number>(0);
  const lastTimeRef = useRef<number>(0);
  const animationRef = useRef<number | null>(null);

  const computeAngle = useCallback((clientX: number, clientY: number) => {
    const { x, y } = centerRef.current;
    const dx = clientX - x;
    const dy = clientY - y;
    return (Math.atan2(dy, dx) * 180) / Math.PI;
  }, []);

  const updateCenter = useCallback(() => {
    if (!mainGearRef.current) return;
    const rect = mainGearRef.current.getBoundingClientRect();
    centerRef.current = {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
    };
  }, []);

  const handlePointerDown = (e: React.PointerEvent) => {
    e.preventDefault();
    if (animationRef.current !== null) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    updateCenter();
    lastAngleRef.current = computeAngle(e.clientX, e.clientY);
    accumulatedRef.current = rotation;
    velocityRef.current = 0;
    lastTimeRef.current = performance.now();
    setIsDragging(true);
    setHasInteracted(true);
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDragging) return;
    const angle = computeAngle(e.clientX, e.clientY);
    let delta = angle - lastAngleRef.current;
    if (delta > 180) delta -= 360;
    if (delta < -180) delta += 360;

    accumulatedRef.current += delta;
    lastAngleRef.current = angle;

    const now = performance.now();
    const dt = now - lastTimeRef.current;
    if (dt > 0) {
      velocityRef.current = delta / dt;
    }
    lastTimeRef.current = now;

    setRotation(accumulatedRef.current);
  };

  const startInertia = useCallback(() => {
    const friction = 0.96;
    const minSpeed = 0.0005;

    const tick = () => {
      velocityRef.current *= friction;
      if (Math.abs(velocityRef.current) < minSpeed) {
        animationRef.current = null;
        return;
      }
      accumulatedRef.current += velocityRef.current * 16;
      setRotation(accumulatedRef.current);
      animationRef.current = requestAnimationFrame(tick);
    };
    animationRef.current = requestAnimationFrame(tick);
  }, []);

  const handlePointerUp = (e: React.PointerEvent) => {
    if (!isDragging) return;
    setIsDragging(false);
    try {
      (e.target as HTMLElement).releasePointerCapture(e.pointerId);
    } catch (_) {
      // noop
    }
    if (Math.abs(velocityRef.current) > 0.005) {
      startInertia();
    }
  };

  useEffect(() => {
    return () => {
      if (animationRef.current !== null) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  const center = containerSize / 2;

  return (
    <div
      className="relative select-none"
      style={{ width: containerSize, height: containerSize }}
    >
      {/* Hint label */}
      {!hasInteracted && (
        <div
          className="absolute z-30 flex items-center gap-2 px-3 py-1.5 rounded-full bg-foreground-950 text-background-50 text-xs font-label tracking-wider uppercase whitespace-nowrap"
          style={{
            top: center,
            left: center,
            transform: `translate(-50%, calc(-${size / 2}px - 36px))`,
          }}
        >
          <i className="ri-drag-move-2-line" />
          Drag the red gear
        </div>
      )}

      {/* Direct satellites — perfectly meshed with main, counter-rotating */}
      {satellites.map((s) => {
        const dist = (size + s.size) / 2;
        const rad = (s.angle * Math.PI) / 180;
        const sx = center + Math.cos(rad) * dist - s.size / 2;
        const sy = center + Math.sin(rad) * dist - s.size / 2;

        const satRotation = -rotation * (MAIN_TEETH / s.teeth) + s.phase;

        return (
          <div
            key={s.id}
            className="absolute pointer-events-none"
            style={{
              width: s.size,
              height: s.size,
              left: sx,
              top: sy,
            }}
          >
            <GearVisual
              size={s.size}
              teeth={s.teeth}
              rotation={satRotation}
              variant={s.variant}
            />
          </div>
        );
      })}

      {/* Chained gears — meshed with a satellite, driven through it */}
      {chained.map((c) => {
        const parent = satellites.find((s) => s.id === c.parentId);
        if (!parent) return null;

        // Parent absolute position
        const pDist = (size + parent.size) / 2;
        const pRad = (parent.angle * Math.PI) / 180;
        const px = center + Math.cos(pRad) * pDist;
        const py = center + Math.sin(pRad) * pDist;

        // Child position relative to parent
        const cDist = (parent.size + c.size) / 2;
        const cRad = (c.angleFromParent * Math.PI) / 180;
        const cx = px + Math.cos(cRad) * cDist - c.size / 2;
        const cy = py + Math.sin(cRad) * cDist - c.size / 2;

        // Parent rotates at: -rotation * (MAIN_TEETH / parent.teeth) + parent.phase
        // Child counter-rotates from parent at parent.teeth / c.teeth
        // → child rotates at +rotation * (MAIN_TEETH / c.teeth) - parent.phase * (parent.teeth / c.teeth) + c.phase
        const childRotation =
          rotation * (MAIN_TEETH / c.teeth) -
          parent.phase * (parent.teeth / c.teeth) +
          c.phase;

        return (
          <div
            key={c.id}
            className="absolute pointer-events-none"
            style={{
              width: c.size,
              height: c.size,
              left: cx,
              top: cy,
            }}
          >
            <GearVisual
              size={c.size}
              teeth={c.teeth}
              rotation={childRotation}
              variant={c.variant}
            />
          </div>
        );
      })}

      {/* Main red gear (draggable) */}
      <div
        ref={mainGearRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        className={`absolute touch-none z-10 ${
          isDragging ? "cursor-grabbing" : "cursor-grab"
        }`}
        style={{
          width: size,
          height: size,
          left: center - size / 2,
          top: center - size / 2,
          transition: isDragging ? "none" : "transform 0.05s linear",
          willChange: "transform",
        }}
      >
        <GearVisual
          size={size}
          teeth={MAIN_TEETH}
          rotation={rotation}
          variant="red"
          showBolt
        />
      </div>
    </div>
  );
}